/**
 * continuitySensitivityEngine.js
 *
 * Determines HOW EXPLICITLY Kevia should surface its remembered continuity.
 *
 * Because sometimes acknowledging memory is pastoral,
 * and sometimes it's invasive, surveillance-like, or artificial.
 *
 * Modes:
 *   explicit  — Kevia can reference the process openly ("has venido caminando esto")
 *   implicit  — Kevia is shaped by memory but doesn't reference it directly
 *   silent    — Kevia acts from knowledge but says nothing about the history
 *
 * Recall depth:
 *   full   — surface milestones, arc, pattern description
 *   soft   — only the arc's instructional posture
 *   none   — inject nothing (override signal gate)
 */

/**
 * @param {{
 *   hasJourneyHistory:   boolean,
 *   hasMilestones:       boolean,
 *   conversationLength:  number,
 *   emotion:             object|null,
 *   processState:        string,
 *   regressionType:      string,
 *   intent:              string,
 * }} params
 * @returns {{
 *   mode:        "explicit"|"implicit"|"silent",
 *   recallDepth: "full"|"soft"|"none",
 *   instruction: string|null,
 * }}
 */
export function assessContinuitySensitivity({
  hasJourneyHistory,
  hasMilestones,
  conversationLength = 0,
  emotion,
  processState,
  regressionType,
  intent,
}) {
  const dom = emotion?.dominantEmotion ?? "none";

  // No history → silent
  if (!hasJourneyHistory && !hasMilestones) {
    return { mode: "silent", recallDepth: "none", instruction: null };
  }

  // Quebranto or active crisis + short conversation → implicit only
  // Don't remind someone of their struggles immediately — let them speak first
  if (
    (processState === "quebranto" || processState === "resistencia") &&
    conversationLength < 4
  ) {
    return {
      mode: "implicit",
      recallDepth: "soft",
      instruction:
        "Hay memoria de un proceso difícil. No lo menciones directamente — deja que la persona hable primero.",
    };
  }

  // Regression detected → implicit, not explicit — avoid adding weight
  if (regressionType === "regression" || regressionType === "sustained_regression") {
    return {
      mode: "implicit",
      recallDepth: "soft",
      instruction: null,
    };
  }

  // Planning/analysis intent = pastor is in operational mode, not pastoral care
  // Full recall would feel out of place
  if (intent === "planning" || intent === "analysis") {
    return {
      mode: "silent",
      recallDepth: "none",
      instruction: null,
    };
  }

  // Sustained restoration + milestones → explicit OK, celebrate appropriately
  if (regressionType === "sustained_restoration" && hasMilestones) {
    return {
      mode: "explicit",
      recallDepth: "full",
      instruction:
        "Hay un proceso de restauración sostenido con hitos reales. Puedes reconocerlo con calidez pastoral.",
    };
  }

  // General: have history and decent conversation depth → implicit
  if (conversationLength >= 3) {
    return {
      mode: "implicit",
      recallDepth: "soft",
      instruction: null,
    };
  }

  return { mode: "silent", recallDepth: "none", instruction: null };
}
